import { scheme } from "vega";
import type { VisualizationSpec } from "vega-embed";
import type { ChartFormatValues } from "../../components/registry";
import type { ChartColorPalette } from "../../components/molecules/ChartColorPaletteMenu";
import type { ChartFormatSeed } from "./chartStudioFormat.types";
import { numberFormatKey, numberFormatLabel } from "./chartNumberFormats";
import { findBuiltInPalette } from "./paletteRegistry";
import {
  DEFAULT_LEGEND_POSITION,
  legendPositionFromOrient,
} from "./legendPosition";

type SpecObject = Record<string, unknown>;

/**
 * Reads back what a chart's Vega-Lite spec actually states, so the Format tab
 * can open showing the chart's own formatting instead of registry fixtures.
 * This is the reader half of the spec adapter; `applyPaletteToSpec` is the
 * writer half, and `readSpecColors` below is its exact inverse.
 *
 * The return type is deliberately `Partial`: a Vega-Lite spec is a *declaration*,
 * not a rendered result. Most properties are optional and Vega computes the
 * rest at render time — `labelAngle` in particular is auto-rotated to fit the
 * available width when the spec omits it. So "the spec does not say" and "the
 * value is 0" are different facts, and only an absent key can express the first
 * one honestly. Every caller falls back to `chartFormatDefaults` for keys this
 * reader leaves out; nothing here guesses at a rendered value.
 */

const asObject = (value: unknown): SpecObject | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as SpecObject)
    : undefined;

/**
 * Flattens the two spec shapes Chart Studio supports to a list of leaves that
 * can carry an `encoding` — the same pair `applyPaletteToSpec` walks, so the
 * reader and the writer can never disagree about where formatting lives.
 */
const unitSpecs = (spec: SpecObject): SpecObject[] => {
  if (!Array.isArray(spec.layer)) return [spec];
  return (spec.layer as unknown[])
    .map(asObject)
    .filter((layer): layer is SpecObject => layer !== undefined);
};

/**
 * First layer that mentions the channel wins, matching how Vega-Lite resolves
 * a shared axis across layers. Returning the raw value rather than an object
 * keeps `null` distinguishable from absent — `axis: null` means "hidden", while
 * no `axis` key at all means "Vega's default", and those map to different
 * answers.
 */
const readChannel = (units: SpecObject[], channel: string): unknown => {
  for (const unit of units) {
    const encoding = asObject(unit.encoding);
    if (encoding && channel in encoding) return encoding[channel];
  }
  return undefined;
};

/** `title` accepts a bare string or `{ text }`; `null` suppresses the title. */
export const readTitleText = (title: unknown): string | null => {
  if (typeof title === "string") return title;
  const titleObject = asObject(title);
  if (!titleObject) return null;
  const { text } = titleObject;
  if (typeof text === "string") return text;
  if (Array.isArray(text)) return text.filter((line) => typeof line === "string").join(" ");
  return null;
};

/**
 * The colours a spec is currently painted with, in palette order. Mirrors
 * `applyPaletteToSpec`'s two supported encodings: a scaled range for
 * field-driven colour, or a single static shade. Named categorical schemes
 * are resolved through Vega's installed registry.
 */
export const readSpecColors = (spec: VisualizationSpec): string[] => {
  const color = asObject(readChannel(unitSpecs((spec as SpecObject) ?? {}), "color"));
  if (!color) return [];
  const range = asObject(color.scale)?.range;
  if (Array.isArray(range)) return range.filter((shade): shade is string => typeof shade === "string");
  if (typeof color.value === "string") return [color.value];
  const schemeName = asObject(color.scale)?.scheme;
  if (typeof schemeName === "string") {
    const colors: unknown = scheme(schemeName);
    // Continuous schemes return interpolators, not a categorical palette.
    if (Array.isArray(colors) && colors.every((shade) => typeof shade === "string")) {
      return [...colors];
    }
  }
  return [];
};

/**
 * Which `ChartFormatValues` keys each axis channel feeds. Exported because
 * `applyChartFormatToSpec` writes back through the same table — one map means
 * the reader and the writer cannot disagree about which key owns which axis
 * property.
 */
export const AXIS_KEYS = {
  x: {
    show: "showXAxis",
    showTitle: "showXAxisTitle",
    title: "xAxisTitle",
    angle: "xAxisLabelAngle",
    grid: "showXAxisGridlines",
  },
  y: {
    show: "showYAxis",
    showTitle: "showYAxisTitle",
    title: "yAxisTitle",
    angle: "yAxisLabelAngle",
    grid: "showYAxisGridlines",
  },
} as const;

/**
 * Vega-Lite's own default axis title, reproduced: the field name, or
 * "Sum of revenue" when the channel aggregates. This is what the chart puts on
 * screen when the spec states no title, so it is what the panel must show.
 */
const derivedAxisTitle = (definition: SpecObject): string | undefined => {
  const { field, aggregate } = definition;
  if (typeof field !== "string") {
    // `{ aggregate: "count" }` is the one aggregate that needs no field.
    return aggregate === "count" ? "Count of Records" : undefined;
  }
  if (typeof aggregate === "string") {
    return `${aggregate.charAt(0).toUpperCase()}${aggregate.slice(1)} of ${field}`;
  }
  return field;
};

const readAxis = (
  units: SpecObject[],
  channel: "x" | "y",
  configAxis: SpecObject | undefined,
  values: Partial<ChartFormatValues>,
): void => {
  const channelDef = readChannel(units, channel);
  if (channelDef === undefined) return;

  const keys = AXIS_KEYS[channel];
  const definition = asObject(channelDef);
  if (!definition) return;

  // `axis: null` is Vega-Lite's way of hiding an axis outright; any other
  // shape leaves it drawn, which is the default for an encoded channel.
  const axisHidden = "axis" in definition && definition.axis === null;
  values[keys.show] = !axisHidden;
  if (axisHidden) return;

  const axis = asObject(definition.axis);

  // A title set on the channel wins over one set on the axis object, matching
  // Vega-Lite's own precedence.
  const titleSource = "title" in definition ? definition.title : axis?.title;
  if (titleSource !== undefined) {
    const title = readTitleText(titleSource);
    values[keys.showTitle] = title !== null;
    if (title !== null) values[keys.title] = title;
  } else {
    // No explicit title does NOT mean no title on screen: Vega-Lite derives one
    // from the field name, so the axis still renders "Category". Falling back to
    // a registry default here would put a word in the panel that contradicts the
    // chart the user is looking at.
    const derived = derivedAxisTitle(definition);
    if (derived !== undefined) {
      values[keys.showTitle] = true;
      values[keys.title] = derived;
    }
  }

  // Spec-level angle beats config-level; with neither, Vega auto-rotates at
  // render time and the spec genuinely has no answer, so the key stays absent.
  const labelAngle = axis?.labelAngle ?? configAxis?.labelAngle;
  if (typeof labelAngle === "number") values[keys.angle] = labelAngle;

  const grid = axis?.grid ?? configAxis?.grid;
  if (typeof grid === "boolean") {
    values[keys.grid] = grid;
  } else {
    // No explicit `grid` does NOT mean no gridlines on screen: Vega-Lite
    // defaults grid to true for quantitative/temporal scales and false for
    // nominal/ordinal ones. Falling back to a flat default here would show
    // the toggle off while the chart already renders gridlines (or vice
    // versa), so the panel must reproduce Vega's own per-scale-type default.
    const scaleType = definition.type;
    values[keys.grid] = scaleType === "quantitative" || scaleType === "temporal";
  }

  // Axis labels are the "category names on the chart" the Labels toggle means.
  // Read from the x-axis only — it is the categorical axis in every chart the
  // canvas currently renders, and two channels cannot drive one toggle.
  if (channel === "x") {
    const labels = axis?.labels ?? configAxis?.labels;
    if (typeof labels === "boolean") values.showLabels = labels;
  }
};

const readLegend = (units: SpecObject[], values: Partial<ChartFormatValues>): void => {
  const color = asObject(readChannel(units, "color"));
  if (!color) return;

  // A static `color.value` paints every mark the same shade, so there are no
  // categories to list and Vega draws no legend at all.
  if (!("field" in color)) {
    values.showLegends = false;
    return;
  }

  // Vega-Lite's own way to hide a legend is `legend: null`, but specs this
  // panel reads also arrive with `legend: false` for the same intent — both
  // must read as "off," not just the spec-correct spelling.
  if ("legend" in color && (color.legend === null || color.legend === false)) {
    values.showLegends = false;
    return;
  }
  values.showLegends = true;

  // A spec that states no orient is not "no position" — Vega still draws the
  // legend somewhere, and reporting where is what makes the highlighted button
  // describe the chart on screen. It is also the baseline the writer diffs
  // against, so reporting nothing here is what used to make the picker's own
  // default unwritable.
  const orient = asObject(color.legend)?.orient;
  const position =
    typeof orient === "string" ? legendPositionFromOrient(orient) : DEFAULT_LEGEND_POSITION;
  values.legendPosition = position ?? DEFAULT_LEGEND_POSITION;
};

/**
 * The channel carrying the numbers a format applies to. `y` first, because
 * every chart the canvas renders today puts its measure there; `x` covers a
 * horizontal bar. A channel counts as the measure when it says so (`type:
 * "quantitative"`) or aggregates, which implies it.
 */
export const isMeasureDefinition = (definition: SpecObject | undefined): boolean =>
  definition !== undefined &&
  (definition.type === "quantitative" || typeof definition.aggregate === "string");

export const MEASURE_CHANNELS = ["y", "x"] as const;

export const measureDefinition = (units: SpecObject[]): SpecObject | undefined => {
  for (const channel of MEASURE_CHANNELS) {
    const definition = asObject(readChannel(units, channel));
    if (isMeasureDefinition(definition)) return definition;
  }
  return undefined;
};

/**
 * Number formatting is a single d3 specifier on the measure axis, so the spec
 * can only ever answer ONE of the two panel rows. The specifier's own type says
 * which: `$~s` is a currency sample, `.1%` a percentage one. A specifier the
 * menus do not offer (the backend's own, or a hand-written spec) leaves both
 * keys absent, and the panel keeps showing its Figma samples rather than
 * claiming the chart uses an option it does not.
 */
const readNumberFormat = (
  units: SpecObject[],
  configNumberFormat: unknown,
  values: Partial<ChartFormatValues>,
): void => {
  const measure = measureDefinition(units);
  const format = asObject(measure?.axis)?.format ?? configNumberFormat;
  if (typeof format !== "string") return;
  const key = numberFormatKey(format);
  const label = numberFormatLabel(format);
  if (key && label) values[key] = label;
};

const readTooltip = (units: SpecObject[], values: Partial<ChartFormatValues>): void => {
  // Either a `tooltip` encoding channel or `mark.tooltip` enables it; the two
  // are alternatives in Vega-Lite, not a precedence chain.
  const hasTooltipChannel = readChannel(units, "tooltip") !== undefined;
  if (hasTooltipChannel) {
    values.showTooltip = true;
    return;
  }
  for (const unit of units) {
    const tooltip = asObject(unit.mark)?.tooltip;
    if (tooltip === true || asObject(tooltip) !== undefined) {
      values.showTooltip = true;
      return;
    }
    if (tooltip === false || tooltip === null) {
      values.showTooltip = false;
      return;
    }
  }
  // Vega-Lite draws no tooltip unless one is asked for, so silence means off.
  values.showTooltip = false;
};

const markType = (unit: SpecObject): string | undefined => {
  if (typeof unit.mark === "string") return unit.mark;
  const type = asObject(unit.mark)?.type;
  return typeof type === "string" ? type : undefined;
};

export const readChartFormat = (
  spec: VisualizationSpec,
  fallbackTitle?: string,
): Partial<ChartFormatValues> => {
  const root = asObject(spec);
  if (!root) return {};

  const units = unitSpecs(root);
  const configAxis = asObject(asObject(root.config)?.axis);
  const values: Partial<ChartFormatValues> = {};

  // Vega-Lite's own `title` is not what a viewer actually sees: every chart
  // renders its title as the widget's own header (ChartWidget.tsx's `<Title>`),
  // driven by the widget's display name, not by anything inside the spec. So
  // the toggle and text field both treat the widget's title as the ground
  // truth, and only fall through to "no title" when neither the spec nor the
  // widget names one — a spec-level title (rare; most specs carry none) still
  // wins when a chart genuinely sets its own.
  const chartTitle = readTitleText(root.title) ?? (fallbackTitle || null);
  values.showChartTitle = chartTitle !== null;
  if (chartTitle !== null) values.chartTitle = chartTitle;

  readAxis(units, "x", configAxis, values);
  readAxis(units, "y", configAxis, values);
  readLegend(units, values);
  readNumberFormat(units, asObject(root.config)?.numberFormat, values);
  readTooltip(units, values);

  // Numbers printed on the marks are their own text layer in Vega-Lite. A
  // non-layered spec has nowhere to put that layer, so it is definitionally
  // off — leaving this unset here would fall back to the default (`true`)
  // and make the diff think the layer already exists, silently swallowing
  // every toggle-on.
  values.showDirectValues = Array.isArray(root.layer)
    ? units.some(
        (unit) => markType(unit) === "text" && asObject(unit.encoding)?.text !== undefined,
      )
    : false;

  return values;
};

/**
 * Shown in the Format tab's Colors row when a chart's colours do not come from
 * any palette in the library. Saying "Custom" is the honest read — naming a
 * library palette the chart is not actually painted with would be a lie the
 * user cannot see through.
 */
export const CUSTOM_PALETTE_LABEL = "Custom";

/** `#ABC` and `#aabbcc` are the same colour; specs and tokens disagree on case. */
const normaliseHex = (color: string): string => {
  const hex = color.trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(hex)) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return hex;
};

/**
 * Maps the colours a chart is painted with back to the palette they came from.
 *
 * The match is a **prefix**, not an equality: Vega only carries as many shades
 * in a `scale.range` as the chart has categories, and a static `color.value`
 * carries exactly one. A four-region bar chart painted from a five-shade
 * palette therefore holds four of those five, in order — so the spec's colours
 * are a prefix of the palette's, and requiring equality would fail to match
 * every real chart.
 *
 * Returns `""` (the state model's "no palette bound") when nothing matches,
 * which is the truthful answer for a chart the backend coloured independently.
 * First match wins if two palettes share a leading run of shades.
 */
export const matchPaletteId = (colors: string[], palettes: ChartColorPalette[]): string => {
  if (colors.length === 0) return "";
  const specColors = colors.map(normaliseHex);
  const match = palettes.find((palette) => {
    const libraryColors = palette.colors.map(normaliseHex);
    return (
      specColors.length <= libraryColors.length &&
      specColors.every((color, index) => color === libraryColors[index])
    );
  });
  return match?.id ?? "";
};

/**
 * The reader's public entry point: one chart's spec becomes the exact
 * `ChartFormatSeed` payload. The reducer registers its palette and stores the
 * remaining formatting entry together.
 *
 * Layering is deliberate — `defaults` first, then what the spec actually states,
 * then the palette label. The middle layer is a `Partial`, so any key the spec
 * is silent about keeps the registry default instead of being overwritten with
 * a guess. See the `Partial` note at the top of this file.
 */
export const readChartFormatEntry = (
  spec: VisualizationSpec,
  palettes: ChartColorPalette[],
  defaults: ChartFormatValues,
  fallbackTitle?: string,
): ChartFormatSeed => {
  const colors = readSpecColors(spec);
  const matchedId = matchPaletteId(colors, palettes);
  let palette = palettes.find((entry) => entry.id === matchedId);
  if (!palette && colors.length > 0) {
    const color = asObject(readChannel(unitSpecs(asObject(spec) ?? {}), "color"));
    const schemeName = asObject(color?.scale)?.scheme;
    palette = {
      // Include the colors so an edited scheme never replaces its original on another chart.
      id: `vega-${colors.map(normaliseHex).map(encodeURIComponent).join("-")}`,
      label: typeof schemeName === "string" && !Array.isArray(asObject(color?.scale)?.range)
        ? (findBuiltInPalette(schemeName)?.label ?? schemeName)
        : CUSTOM_PALETTE_LABEL,
      colors,
    };
    // A palette with this original id may already have been edited in the library.
    if (palettes.some((entry) => entry.id === palette?.id)) {
      palette.id = `${palette.id}-${palettes.length}`;
    }
  }
  const selectedPaletteId = palette?.id ?? "";
  return {
    selectedPaletteId,
    palette,
    initialPaletteColors: palette ? [...palette.colors] : [],
    values: {
      ...defaults,
      ...readChartFormat(spec, fallbackTitle),
      colorPalette: palette?.label ?? CUSTOM_PALETTE_LABEL,
    },
  };
};
